/**
 * One tappable teacher card layout, used by TeacherPicker (full-screen,
 * primary/disclosure split) — kept in its own file so the card markup isn't
 * duplicated across pickers.
 *
 * rb3↔rb5 sibling toggle (2026-08-06): a teacher whose groups are ALL at the
 * sibling location gets a small badge with that location's code — a
 * sibling-only teacher would otherwise look indistinguishable from a
 * home-location one in this list.
 */

import { Badge } from '@/components/ui/badge';
import { InitialsAvatar } from '@/components/ui/initials-avatar';
import type { LocationSessionTeacher, LocationTeacherToday } from '../types';

function toSessionTeacher(t: LocationTeacherToday): LocationSessionTeacher {
  return { staffId: t.staffId, firstName: t.firstName, fullName: t.fullName };
}

/** Null unless every one of the teacher's groups sits at a single other location. */
function siblingBadgeCode(t: LocationTeacherToday, locationCode: string): string | null {
  const codes = t.groupLocationCodes ?? [];
  if (codes.length === 0 || codes.some((c) => c === locationCode)) return null;
  return codes[0];
}

export function PersonList({
  teachers,
  locationCode,
  onPick,
}: {
  teachers: LocationTeacherToday[];
  /** Own location code — used to tell an all-sibling teacher apart for the badge. */
  locationCode: string;
  onPick: (teacher: LocationSessionTeacher) => void;
}) {
  return (
    <div className="flex flex-col gap-2.5">
      {teachers.map((t) => {
        const badgeCode = siblingBadgeCode(t, locationCode);
        return (
          <button
            key={t.staffId}
            type="button"
            onClick={() => onPick(toSessionTeacher(t))}
            className="flex items-center gap-3 rounded-2xl border-2 border-transparent bg-card px-3.5 py-3 text-left shadow-sm transition active:scale-[0.98] active:border-primary/30"
          >
            <InitialsAvatar name={t.fullName} size={40} />
            <span className="text-sm font-extrabold text-foreground">{t.firstName}</span>
            {badgeCode && (
              <Badge variant="secondary" className="text-[10px] px-1.5 h-5">
                {badgeCode.toUpperCase()}
              </Badge>
            )}
          </button>
        );
      })}
    </div>
  );
}
